import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, NavLink } from "react-router-dom";
import { toast } from "react-toastify";
import { FaGoogle, FaFacebookF, FaEye, FaEyeSlash } from "react-icons/fa";
import { register, reset } from "../features/auth/authSlice";
import Spinner from "../components/utils/Spinner";
import { closeMenu } from "../features/utils/menuSlice";
import { Button, Form, Container, Row, Col, InputGroup } from "react-bootstrap";
import '../styles/authShared.css';

function Register() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    password2: "",
    termsCheck: false
  });

  const { name, email, password, password2, termsCheck } = formData;

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { user, isLoading, isError, isSuccess, message } = useSelector(
    (state) => state.auth
  );

  useEffect(() => {
    if (isError) {
      toast.error(message);
    }

    if (isSuccess || user) {
      navigate("/");
    }

    dispatch(reset());
  }, [user, isError, isSuccess, message, navigate, dispatch]);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword((prev) => !prev);
  };

  const onChange = (e) => {
    setFormData((prevState) => ({
      ...prevState,
      [e.target.name]: e.target.value,
    }));
  };

  const onChangeTermsCheck = (e) => {
    setFormData((prevState) => ({
      ...prevState,
      [e.target.name]: !prevState[e.target.name],
    }));
  };

  const lengthValidator = (form) => {
    for (const property in form) {
      if (typeof form[property] === "string" && form[property].length < 3) {
        toast.error(`Please fill the field "${property}" properly`);
        return false;
      }
    }
    return true;
  };

  const onSubmit = (e) => {
    e.preventDefault();

    if (!formData["termsCheck"]) {
      toast.error("Please accept the terms and conditions");
      return;
    }

    if (!lengthValidator(formData)) {
      return;
    }

    if (password !== password2) {
      toast.error("Passwords do not match");
      return;
    }

    const userData = {
      name,
      email,
      password,
    };

    dispatch(register(userData));
  };

  if (isLoading) {
    return <Spinner />;
  }

  return (
    <Container className="d-flex justify-content-center">
      <div className="auth-card card border-0 shadow-lg">
        <div className="card-body p-4 p-md-5">
          {/* Header */}
          <div className="auth-header">
            <h1 className="display-5 fw-bold mb-2 text-dark">Create Your Account</h1>
            <p className="text-muted">Join the Atelier to start managing your finances.</p>
          </div>

          {/* Social Login */}
          <Row className="g-3 mb-4 auth-social-section">
            <Col>
              <Button
                variant="outline-secondary"
                className="w-100 auth-social-btn d-flex align-items-center justify-content-center gap-2"
              >
                <FaGoogle className="auth-social-btn-google" />
              </Button>
            </Col>
            <Col>
              <Button
                variant="outline-secondary"
                className="w-100 auth-social-btn d-flex align-items-center justify-content-center gap-2"
              >
                <FaFacebookF className="auth-social-btn-facebook" />
              </Button>
            </Col>
          </Row>

          {/* Divider */}
          <div className="divider d-flex align-items-center my-4">
            <hr className="flex-grow-1" />
            <span className="px-3 small text-uppercase text-muted fw-bold">or continue with</span>
            <hr className="flex-grow-1" />
          </div>

          {/* Form */}
          <Form onSubmit={onSubmit}>
            {/* Full Name */}
            <div className="mb-4">
              <Form.Label className="fw-bold small text-uppercase mb-2">
                Full Name
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="John Doe"
                className="auth-input"
                value={name}
                name="name"
                onChange={onChange}
                required
              />
            </div>

            {/* Email */}
            <div className="mb-4">
              <Form.Label className="fw-bold small text-uppercase mb-2">
                Email Address
              </Form.Label>
              <Form.Control
                type="email"
                placeholder="your@email.com"
                className="auth-input"
                value={email}
                name="email"
                autoComplete="email"
                onChange={onChange}
                required
              />
            </div>

            {/* Password */}
            <div className="mb-4">
              <Form.Label className="fw-bold small text-uppercase mb-2">
                Password
              </Form.Label>
              <InputGroup className="d-flex align-items-center">
                <Form.Control
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  className="auth-input"
                  value={password}
                  onChange={onChange}
                  name="password"
                  required
                />
                <InputGroup.Text
                  className="bg-unset cursor-pointer me-1 h-75"
                  onClick={togglePasswordVisibility}
                  style={{ border: "none" }}
                >
                  {showPassword ? (
                    <FaEyeSlash className="text-muted" />
                  ) : (
                    <FaEye className="text-muted" />
                  )}
                </InputGroup.Text>
              </InputGroup>
            </div>

            {/* Confirm Password */}
            <div className="mb-4">
              <Form.Label className="fw-bold small text-uppercase mb-2">
                Confirm Password
              </Form.Label>
              <InputGroup className="d-flex align-items-center">
                <Form.Control
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="••••••••"
                  className="auth-input"
                  value={password2}
                  onChange={onChange}
                  name="password2"
                  required
                />
                <InputGroup.Text
                  className="bg-unset cursor-pointer me-1 h-75"
                  onClick={toggleConfirmPasswordVisibility}
                  style={{ border: "none" }}
                >
                  {showConfirmPassword ? (
                    <FaEyeSlash className="text-muted" />
                  ) : (
                    <FaEye className="text-muted" />
                  )}
                </InputGroup.Text>
              </InputGroup>
            </div>

            {/* Terms and Conditions */}
            <div className="mb-4 auth-terms-section">
              <Form.Check
                type="checkbox"
                id="termsCheck"
                name="termsCheck"
                checked={termsCheck}
                onChange={onChangeTermsCheck}
                className="auth-terms-label"
                label={
                  <small>
                    I agree to the "
                    <NavLink to="/" className="text-primary fw-bold">
                      Terms and Conditions
                    </NavLink>"
                  </small>
                }
              />
            </div>

            {/* Submit Button */}
            <div className="d-grid mb-4">
              <Button
                variant="primary"
                className="auth-btn-primary py-3 fw-bold shadow-sm"
                type="submit"
              >
                Create Account
              </Button>
            </div>
          </Form>

          {/* Sign In Link */}
          <div className="text-center mb-4">
            <span className="text-muted">Already have an account? </span>
            <NavLink to="/login" className="fw-bold text-dark">
              Sign in
            </NavLink>
          </div>
        </div>
      </div>
    </Container>
  );
}

export default Register;
